import React, {type ComponentClass, type ReactNode} from 'react';

import {skipToken} from '@reduxjs/toolkit/query';
import {withRouter, type WithRouterProps} from 'react-router';

import {usePanelsOnDifferentPageSizes} from 'app/components/general/DetailPage/hooks';
import {RightSidebar} from 'app/components/general/DetailPage/RightSidebar/RightSidebar';
import {SPPage} from 'app/general/structures/SPPage';
import {gt} from 'app/utils/i18n';

import type {
  LeftSidebarElementType,
  LeftSidebarProps,
} from 'app/components/general/DetailPage/LeftSidebar/structure/types';
import type {StickySidebarElementType} from 'app/components/general/DetailPage/RightSidebar/structure/types';
import type {
  CurrentItemType,
  CurrentVersionType,
  DetailPageItemType,
} from 'app/components/general/DetailPage/types';
import type {PermsMap} from 'app/current-user/types';
import type {UseQueryType} from 'app/store/sweet-api/api-query';

type Props<ItemType extends DetailPageItemType> = {
  breadcrumbProps?: Record<string, unknown>;
  useHashidHook: UseQueryType<string, ItemType>;
  useSelectedVersionHook: UseQueryType<
    {versionId: string | number},
    CurrentVersionType<ItemType>
  >;
  LeftSidebar?: ComponentClass<
    Omit<LeftSidebarProps<ItemType>, keyof WithRouterProps>
  >;
  CurrentLeftSidebar?: LeftSidebarElementType<DetailPageItemType>;
  CurrentRightSidebar?: StickySidebarElementType<DetailPageItemType>;
  rightSidebarPanels: Array<StickySidebarElementType<DetailPageItemType>>;
  actionArea?: ReactNode | ((perms: PermsMap) => ReactNode);
  versionbar?: ReactNode;
  children: ReactNode;
  context: 'view' | 'edit';
};

export const PageWithRightSidebar = withRouter<
  Props<CurrentItemType<DetailPageItemType>>
>(
  ({
    useHashidHook,
    useSelectedVersionHook,
    LeftSidebar,
    CurrentLeftSidebar,
    CurrentRightSidebar,
    rightSidebarPanels,
    children,
    breadcrumbProps,
    actionArea,
    versionbar,
    router,
    params,
    context,
  }: Props<CurrentItemType<DetailPageItemType>> & WithRouterProps) => {
    const {versionid: versionId, hashid: hashId} = params;

    const {data: object, refetch} = useHashidHook(hashId, {
      refetchOnMountOrArgChange: false,
    });
    const {data: version} = useSelectedVersionHook(
      object == null
        ? skipToken
        : {versionId: versionId || object.current_version.id},
      {
        refetchOnMountOrArgChange: false,
      }
    );

    const {currentScreenSize, fullscreenWidget, setFullscreenWidget} =
      usePanelsOnDifferentPageSizes(
        CurrentLeftSidebar,
        router,
        object,
        rightSidebarPanels,
        CurrentRightSidebar
      );

    if (version == null || object == null) {
      return null;
    }

    function getRenderedComponent() {
      if (fullscreenWidget == null || object == null || version == null) {
        return children;
      }

      if (fullscreenWidget.type === 'right') {
        return (
          <fullscreenWidget.Panel
            object={object}
            version={version}
            isFullscreen={true}
            setFullscreenState={(isFullscreen: boolean) =>
              setFullscreenWidget(isFullscreen ? fullscreenWidget : null)
            }
          />
        );
      } else {
        return (
          <fullscreenWidget.Panel
            object={object}
            version={version}
            legacyRefetch={refetch}
          />
        );
      }
    }

    return (
      <SPPage
        pageTitle={
          context === 'edit'
            ? gt('%(name)s | Editing', {
                name: object.name,
              })
            : object.name
        }
        horizontalPadding={false}
        breadcrumbProps={breadcrumbProps}
        actionArea={actionArea}
        sidebar={
          LeftSidebar != null && (
            <LeftSidebar
              CurrentLeftSidebar={
                currentScreenSize === 'sm' ? undefined : CurrentLeftSidebar
              }
              routeName={CurrentLeftSidebar?.sidebarName}
            />
          )
        }
        rightSidebar={
          <RightSidebar
            object={object}
            version={version}
            rightSidebarPanels={rightSidebarPanels}
            fullscreenWidgetName={fullscreenWidget?.sidebarName}
            setFullscreenWidget={setFullscreenWidget}
          />
        }
        versionbar={versionbar}
      >
        {getRenderedComponent()}
      </SPPage>
    );
  }
);
